import React, { type ChangeEvent, type FC, useCallback, useEffect, useId, useState } from 'react'
import { Pagination as BasePagination, Box, InputLabel, OutlinedInput, Stack, type SxProps, type Theme, Typography } from '@mui/material'

import { Translation } from '../../../../domain'

import { FormattedTranslation } from '../../Internationalization'
import { createOnEnterHandler } from '../../utils'
import { Dropdown } from '../Dropdown'
import {
    useBodyState,
    useHideJumpToPage,
    usePage,
    usePageDispatcher,
    usePageSize,
    usePageSizeDispatcher,
    usePageSizeOptions,
    useShowTotalCount,
    useTotalCount,
    useTotalPages,
    useTranslations,
} from './State'

const boxStyle: SxProps<Theme> = { flex: 1 }
const totalCountStyle: SxProps<Theme> = { flex: 1, typography: 'caption', color: 'grey.900' }

const jumpToPageLabelStyle: SxProps<Theme> = { mr: 2, color: 'grey.900' }
const jumpToPageInputStyle: SxProps<Theme> = {
    width: 54,
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
}

const TotalCount: FC | null = () => {
    const totalCount = useTotalCount()
    const pageSize = usePageSize()
    const page = usePage()
    const { paginationDataType: type } = useTranslations()

    if (!(totalCount && pageSize && type)) {
        return null
    }

    const startNum = Math.max(pageSize * page + 1, 1)
    const endNum = Math.min(pageSize * (page + 1), totalCount)
    const showShort = totalCount <= pageSize

    return (
        <Box sx={totalCountStyle} data-testid="totalCount">
            <FormattedTranslation id={Translation.TABLE_PAGINATION_DISPLAYED_ROWS} values={{ startNum, endNum, totalCount, showShort }} />{' '}
            <FormattedTranslation id={Translation[type]} values={{ count: totalCount }} />
        </Box>
    )
}

const JumpToPage: FC = () => {
    const page = usePage() + 1
    const totalPages = useTotalPages()
    const changePage = usePageDispatcher()
    const hideJumpToPage = useHideJumpToPage()

    const inputId = useId()
    const [currentPage, setCurrentPage] = useState<number | ''>(page)

    useEffect(() => setCurrentPage(page), [page])

    const onChange = useCallback(
        ({ target: { valueAsNumber } }: ChangeEvent<HTMLInputElement>) =>
            setCurrentPage(isNaN(valueAsNumber) ? '' : Math.min(Math.max(valueAsNumber, 1), totalPages)),
        [totalPages]
    )

    const onKeyDown = useCallback(
        createOnEnterHandler(() => {
            if (currentPage) {
                changePage(currentPage - 1)
            }
        }),
        [page, currentPage]
    )

    return (
        <Box sx={boxStyle}>
            {!hideJumpToPage && (
                <Stack direction="row" alignItems="center">
                    <Typography component="label" variant="caption" htmlFor={inputId} sx={jumpToPageLabelStyle}>
                        <FormattedTranslation id={Translation.JUMP_TO_PAGE} />
                    </Typography>
                    <OutlinedInput
                        data-testid="jumpToPage"
                        id={inputId}
                        size="small"
                        type="number"
                        sx={jumpToPageInputStyle}
                        value={currentPage}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                    />
                </Stack>
            )}
        </Box>
    )
}

const LeftSidebar: FC = () => {
    const showTotalCount = useShowTotalCount()
    const { paginationDataType } = useTranslations()

    return showTotalCount && paginationDataType ? <TotalCount /> : <JumpToPage />
}

const Pagination: FC = () => {
    const totalPages = useTotalPages()

    const page = usePage()
    const changePage = usePageDispatcher()

    return <BasePagination count={totalPages} page={page + 1} onChange={(_, page) => changePage(page - 1)} />
}

const pageSizeMenuWrapperStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
const pageSizeMenuLabelStyle: SxProps<Theme> = { mr: 2, typography: 'caption', color: 'grey.900' }

const PageSizeMenu: FC = () => {
    const pageSize = usePageSize()
    const changePageSize = usePageSizeDispatcher()
    const pageSizeOptions = usePageSizeOptions()

    return pageSizeOptions && pageSizeOptions.length > 1
        ? (
            <Box sx={[boxStyle, pageSizeMenuWrapperStyle]}>
                <InputLabel variant="standard" sx={pageSizeMenuLabelStyle}>
                    <FormattedTranslation id={Translation.ROWS_PER_PAGE} />
                </InputLabel>
                <Dropdown<number>
                    options={pageSizeOptions.map((value) => ({
                        value,
                        label: value,
                    }))}
                    size="small"
                    data-testid="pagination-rows"
                    value={pageSize}
                    onChange={(value) => changePageSize(value)}
                    buttonLabel={pageSize}
                />
            </Box>
        )
        : <Box sx={boxStyle} />
}

const FOOTER_HEIGHT = 44

const cellStyle: SxProps<Theme> = {
    display: 'flex',
    gap: 4,
    minHeight: FOOTER_HEIGHT,
    px: 6,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: '1px solid',
    borderTopColor: 'grey.300',
}

export const Footer: FC = () => {
    const bodyState = useBodyState()
    const hasBodyContent = bodyState === 'rows' || bodyState === 'reloading'
    const totalPages = useTotalPages()
    const pageSizeOptions = usePageSizeOptions()

    /**
     * If pageSize or pageSizeOptions are falsy,
     * that means we show all data on the table,
     * and there is no need to display pagination
     */
    if (!hasBodyContent || !isFinite(totalPages) || totalPages === 0 || pageSizeOptions === null) {
        return null
    }

    return (
        <Box data-testid="table-footer" sx={cellStyle}>
            <LeftSidebar />
            <Pagination />
            <PageSizeMenu />
        </Box>
    )
}
